<template>
    <div>
        <div v-if="editing">
            <edit-task v-if="activePage === 'tasks.edit'" :event="editedTask" />
            <create-task v-if="activePage === 'tasks.create'" />
        </div>
        <div v-else>
            <div v-if="loading">
                <loading></loading>
            </div>

            <div v-else>
                <b-alert variant="danger" :show="hasErrors">
                    <span v-html="errorMessage"></span>
                </b-alert>

                <!-- filter -->
                <b-row v-if="!hasErrors" class="mb-3">
                    <b-col md="6" class="my-1">
                        <todo-filter />
                    </b-col>

                    <b-col md="6" class="my-1">
                        <b-form-group label-cols-sm="3" class="mb-0">
                            <b-input-group size="sm">
                                <b-form-input
                                    v-model="filter"
                                    placeholder="Entrer la valeur recherchée"
                                ></b-form-input>
                                <b-input-group-append>
                                    <b-button
                                        :disabled="!filter"
                                        @click="filter = ''"
                                        >Effacer</b-button
                                    >
                                </b-input-group-append>
                            </b-input-group>
                        </b-form-group>
                    </b-col>
                </b-row>
                <!-- /filter -->

                <!-- Table -->
                <b-table
                    responsive
                    small
                    :items="filteredItems"
                    :fields="fields"
                    :filter="filter"
                    :per-page="perPage"
                    :current-page="currentPage"
                    @filtered="onFiltered"
                >
                    <template #cell(id)="data">
                        <item-menu :item="data.item"></item-menu>
                    </template>
                    <template #cell(priority)="data">
                        <priority-handler
                            :key="'ph' + data.item.id"
                            :priority="data.item.priority"
                        />
                    </template>
                    <template #cell(created_by)="data">
                        <span>{{ data.item.created_by.name }}</span>
                    </template>
                    <template #cell(assigned_to)="data">
                        <span>{{ data.item.assigned_to.name }}</span>
                    </template>
                    <template #cell(deadline_date)="data">
                        <span>{{
                            data.item.deadline_date | moment('DD/MM/YYYY')
                        }}</span>
                    </template>
                    <template #cell(billable)="data">
                        <billable-handler :status="data.item.billable" />
                    </template>
                    <template #cell(cb)="data">
                        <check-task
                            :cb="data.item.cb"
                            :done="data.item.done"
                            :can-complete="data.item.can_complete"
                        ></check-task>
                    </template>
                </b-table>
                <!-- /Table -->

                <!-- Pagination -->
                <b-row>
                    <b-col md="3" class="my-1 text-center">
                        <b-pagination
                            v-if="totalRows > 0"
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            class="my-0"
                            size="sm"
                        ></b-pagination>
                    </b-col>
                    <b-col md="6" class="my-2">
                        <p class="text-center small pt-0">{{ legend }}</p>
                    </b-col>
                    <b-col md="3" class="my-1 text-right">
                        <b-dropdown
                            right
                            split
                            size="sm"
                            :text="perPage + ' éléments par page'"
                        >
                            <b-dropdown-item
                                v-for="pageOption in pageOptions"
                                :key="pageOption"
                                @click="setPerPage(pageOption)"
                            >
                                {{ pageOption }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-col>
                </b-row>
                <!-- /Pagination -->
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'

import TodoFilter from '../../../activity/components/TodoFilter'

import loading from '../../../../common/components/loading'
import EditTask from './Edit'
import CreateTask from './Create'

import BillableHandler from '../../../../common/components/BillableHandler'
import PriorityHandler from '../../../../common/components/PriorityHandler'
import CheckTask from '../../../../common/components/CheckTask'
import ItemMenu from '../../../../common/components/TaskMenu'

import TableHandler from '../../../../common/mixins/TableHandler'
import TodoFilterHandler from '../../../todo/mixins/TodoFilterHandler'

export default {
    name: 'ClientTasksIndex',

    components: {
        BillableHandler,
        PriorityHandler,
        TodoFilter,
        loading,
        CheckTask,
        ItemMenu,
        EditTask,
        CreateTask,
    },

    mixins: [TableHandler, TodoFilterHandler],

    props: {
        id: 0,
    },

    data() {
        return {
            fields: [
                {
                    key: 'id',
                    label: '',
                    class: 'td-sm',
                    sortable: false,
                },
                {
                    key: 'priority',
                    label: 'Priorité',
                    class: 'td-sm text-center',
                    sortable: true,
                },
                {
                    key: 'description',
                    label: 'Tâche',
                    class: 'td-sm',
                    thStyle: 'width:50%',
                    sortable: true,
                },
                {
                    key: 'assigned_to',
                    label: 'Affectée à',
                    class: 'td-sm',
                    sortable: true,
                },
                {
                    key: 'created_by',
                    label: 'Assignée par',
                    class: 'td-sm',
                    sortable: true,
                },
                {
                    key: 'deadline_date',
                    label: 'Echéance',
                    class: 'td-sm text-center',
                    sortable: true,
                },
                {
                    key: 'billable',
                    label: 'Facturable',
                    class: 'td-sm text-center',
                    sortable: true,
                },
                {
                    key: 'cb',
                    label: 'Fait',
                    class: 'td-sm text-right',
                    sortable: false,
                },
            ],
        }
    },

    created() {
        this.$store.commit('browsing/SET_CLIENT_ID', this.id)
        this.getItems()
        this.$store.commit('browsing/SET_PAGE_NAME', 'tasks.index')
        this.$store.commit('browsing/RESET_EDITING')
    },

    mounted() {
        if (this.browsingFilter === 'done') {
            this.$store.commit('browsing/SET_TODO_FILTER', 'all')
        }
    },

    methods: {
        async getItems() {
            try {
                this.loading = true
                const response = await axios.get(
                    `/clients/${this.clientId}/tasks`
                )
                this.items = response.data.data
                this.loading = false
            } catch (error) {
                console.log(error)
                this.handleErrors(error.response)
            }
        },
    },

    computed: {
        ...mapState({
            clientId: (state) => state.browsing.clientId,
            editing: (state) => state.browsing.editing,
            editedTask: (state) => state.data.editedTask,
            browsingFilter: (state) => state.browsing.deadlineFilter,
            activePage: (state) => state.browsing.pageName,
        }),
    },
}
</script>

<style scoped></style>
